import {
  Form,
  FormRow,
  Option,
  ScreenReaderOnly,
  SegmentedButton,
} from '@op/opds.opux';
import { type SyntheticEvent } from 'react';
import styles from './DeductibleDaysSelect.module.scss';
import coverageSelectorStyles from '../CoverageSelector/CoverageSelector.module.scss';
import { type Premium } from 'dto/CoveragePremium';
import { type Coverage } from 'dto/marketableProduct';
import i18n from 'i18n';
import { useAppStore } from 'state/store';
import { getSelectedOption } from 'utils/premium-utils';

interface Props {
  coverage: Coverage;
  premium: Premium;
  selectablePremiums: Premium[];
}

const DeductibleDaysSelect = ({
  coverage,
  premium,
  selectablePremiums,
}: Props) => {
  const handleSumInsuredChange = useAppStore(
    (state) => state.handleSumInsuredChange
  );

  if (!premium.deductibleDays) {
    throw new Error('Deductible days cannot be undefined');
  }

  const selectionChanged = (_: SyntheticEvent, value: string) => {
    const selectedOption = getSelectedOption(
      premium.sumInsured.toString(),
      selectablePremiums,
      value
    );
    if (selectedOption) {
      handleSumInsuredChange(coverage.coverageProduct, selectedOption);
    }
  };

  return (
    <Form
      onSubmit={(event) => {
        event.preventDefault();
      }}
      stacked
      // @ts-expect-error noValidate does exist on HTMLFormElement
      noValidate>
      <FormRow
        data-testid={`${coverage.coverageProduct}-deductible-days-select-row`}
        label={
          <>
            {i18n.t('common.deductibleDays')}
            <ScreenReaderOnly>
              {i18n.t(`coverageProduct.${coverage.coverageProduct}.name`)}
            </ScreenReaderOnly>
          </>
        }
        labelId={`${coverage.coverageProduct}-deductible-days-label`}
        className={coverageSelectorStyles.noMargin}>
        <SegmentedButton
          onChange={selectionChanged}
          value={premium.deductibleDays.toString()}
          className={styles.deductibleDaysSegmentedButton}
          data-testid={`${coverage.coverageProduct}-deductible-days-segmented-button`}
          aria-describedby={`coverageProduct.${coverage.coverageProduct}.deductibleDaysHelperText`}>
          {coverage.selectableDeductibleDays?.map((deductibleDay) => (
            <Option
              key={deductibleDay}
              value={deductibleDay.toString()}
              data-testid={`${coverage.coverageProduct}-deductible-day-option-${deductibleDay}`}>
              {`${deductibleDay} ${i18n.t('common.day')}`}
            </Option>
          ))}
        </SegmentedButton>
        <div
          className={coverageSelectorStyles.helperText}
          id={`coverageProduct.${coverage.coverageProduct}.deductibleDaysHelperText`}>
          {i18n.t(
            `coverageProduct.${coverage.coverageProduct}.deductibleDaysHelperText`
          )}
        </div>
      </FormRow>
    </Form>
  );
};

export default DeductibleDaysSelect;
